import { useRef, useCallback, useEffect, RefObject } from 'react'

const useBackdropClick = <T extends HTMLElement>(
  cb: () => void,
  selectorArr?: string[]
): RefObject<T | null> => {
  const ref = useRef<T | null>(null)
  // Use a ref instead of React state so touchstart -> touchend doesn't depend on
  // async state updates (can be flaky on some Android/Samsung browsers).
  const isPointerDownInsideRef = useRef<boolean>(false)
  // On some mobile browsers (notably Samsung Internet), a tap can fire touch events
  // followed by synthetic mouse events. If we handle both, outside-click logic can
  // run twice and close popups/dropdowns unexpectedly.
  const lastTouchTimestampRef = useRef<number>(0)

  const checkIsOnOutside = useCallback(
    (e: Event): boolean => {
      const selectors = selectorArr
        ? selectorArr.flatMap(selector =>
            Array.from(document.querySelectorAll(selector))
          )
        : []

      const hasPreventedSelector = selectors.some(selector =>
        selector.contains(e.target as Node)
      )

      return !!(
        ref &&
        ref.current &&
        !ref.current.contains(e.target as Node) &&
        !hasPreventedSelector
      )
    },
    [selectorArr]
  )

  const onMouseUp = useCallback(
    (e: Event) => {
      const isMouseUpOutside = checkIsOnOutside(e)
      if (!isPointerDownInsideRef.current && isMouseUpOutside) {
        return cb()
      }
    },
    [checkIsOnOutside, cb]
  )

  const onMouseDown = useCallback(
    (e: Event) => {
      isPointerDownInsideRef.current = !checkIsOnOutside(e)
    },
    [checkIsOnOutside]
  )

  useEffect(() => {
    const isRecentTouch = () => Date.now() - lastTouchTimestampRef.current < 800

    const handleMouseDown = (e: Event) => {
      if (e.type === 'mousedown' && isRecentTouch()) return
      onMouseDown(e)
    }
    const handleMouseUp = (e: Event) => {
      if (e.type === 'mouseup' && isRecentTouch()) return
      onMouseUp(e)
    }

    const handleTouchStart = (e: Event) => {
      lastTouchTimestampRef.current = Date.now()
      onMouseDown(e)
    }
    const handleTouchEnd = (e: Event) => onMouseUp(e)

    document.body.addEventListener('mousedown', handleMouseDown)
    document.body.addEventListener('mouseup', handleMouseUp)
    document.body.addEventListener('touchstart', handleTouchStart)
    document.body.addEventListener('touchend', handleTouchEnd)
    document.body.addEventListener('touchcancel', handleTouchEnd)

    return () => {
      document.body.removeEventListener('mousedown', handleMouseDown)
      document.body.removeEventListener('mouseup', handleMouseUp)
      document.body.removeEventListener('touchstart', handleTouchStart)
      document.body.removeEventListener('touchend', handleTouchEnd)
      document.body.removeEventListener('touchcancel', handleTouchEnd)
    }
  }, [onMouseDown, onMouseUp])

  return ref
}

export default useBackdropClick
